
import {
  ClearRounded,
  EditRounded,
  VisibilityRounded,
} from "@mui/icons-material";

import {
  Box,
  Button,
  Divider,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";

import React, {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";

import { Alert, Dialog, Table } from "../../layout";
import * as actions from "../../api/actions/pos";
import { endpoints, controller } from "../../api/actions/api";
import { AppContext } from "../../App";

import ReportPrintButton from "../reports/ReportPrintButton";

// Reusable customer details content
const CustomerDetailsContent = ({ customer, t }) => {
  const fields = [
    {
      label: t("customerName"),
      value: customer.customerName,
    },
    {
      label: t("phone"),
      value: customer.phone,
    },
    {
      label: t("email"),
      value: customer.email,
    },
    {
      label: t("address"),
      value: customer.address,
    },
    {
      label: t("city"),
      value: customer.city,
    },
    {
      label: t("state"),
      value: customer.state,
    },
    {
      label: t("zip"),
      value: customer.zip,
    },
    {
      label: t("notes"),
      value: customer.notes,
    },
  ];

  return (
    <Box>
      <Typography
        variant="h5"
        sx={{
          textAlign: "center",
          fontWeight: "bold",
          mb: 3,
        }}
      >
        {t("customerDetails", {
          defaultValue: "Customer Details",
        })}
      </Typography>

      {fields.map((field, index) => (
        <React.Fragment key={index}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              gap: 2,
              py: 1,
            }}
          >
            <Typography
              variant="body1"
              sx={{
                fontWeight: 600,
                minWidth: "35%",
              }}
            >
              {field.label}:
            </Typography>

            <Typography
              variant="body1"
              sx={{
                textAlign: "right",
                whiteSpace: "pre-wrap",
                overflowWrap: "anywhere",
              }}
            >
              {field.value === null ||
              field.value === undefined ||
              field.value === ""
                ? "-"
                : field.value}
            </Typography>
          </Box>

          <Divider />
        </React.Fragment>
      ))}
    </Box>
  );
};

const Customers = (props) => {
  const { t } = useTranslation();

  const {
    _customersList,
    _store,
    _getById,
    _delete,
  } = props;

  const navigate = useNavigate();
  const { setLoader } = useContext(AppContext);

  const reportRef = useRef(null);
  const detailsRef = useRef(null);

  const [showView, setShowView] = useState(false);
  const [viewData, setViewData] = useState(null);

  const [alert, setAlert] = useState({
    isOpen: false,
    type: "",
    title: "",
    subTitle: "",
  });

  //------------------------------------------------
  // Add / Edit Customer
  //------------------------------------------------
  const handleAddEdit = (val = null) => {
    navigate(
      val?.id
        ? `customer-form/${val.id}`
        : "customer-form"
    );
  };

  //------------------------------------------------
  // View Customer
  //------------------------------------------------
  const handleView = (data) => {
    const originalCustomer = (
      _customersList || []
    ).find(
      (customer) =>
        Number(customer.id) === Number(data.id)
    );

    setViewData(originalCustomer || data);
    setShowView(true);
  };

  const handleCloseView = () => {
    setShowView(false);
    setViewData(null);
  };

  //------------------------------------------------
  // Delete Customer
  //------------------------------------------------
  const handleDlt = (id) => {
    setAlert({
      isOpen: true,
      type: "confirmation",
      title: t("deleteCustomer"),
      subTitle: t("deleteCustomerConfirmation"),

      onConfirm: () => {
        setLoader(true);

        const onComplete = (res) => {
          setLoader(false);

          setAlert({
            isOpen: true,
            type:
              res?.status === 200
                ? "success"
                : "error",
            title:
              res?.status === 200
                ? t("deletedSuccessfully")
                : t("error"),
            subTitle:
              res?.msg || t("requestFailed"),
          });
        };

        _delete(
          endpoints.Customer,
          id,
          actions.ActionType.RmvCustomer,
          onComplete
        );
      },
    });
  };

  //------------------------------------------------
  // Load Customers
  //------------------------------------------------
  useEffect(() => {
    if (_store?.id) {
      setLoader(true);

      _getById(
        endpoints.Customer + controller.GetByStore,
        _store.id,
        actions.ActionType.SetCustomers,
        () => setLoader(false)
      );
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_store?.id]);

  //------------------------------------------------
  // Customer Table Data
  //------------------------------------------------
  const tableData = useMemo(() => {
    return Array.isArray(_customersList)
      ? _customersList
      : [];
  }, [_customersList]);

  //------------------------------------------------
  // Table Columns
  //------------------------------------------------
  const screenColumns = [
    {
      id: "actions",
      label: t("actions"),
    },
    {
      id: "customerName",
      label: t("customerName"),
    },
    {
      id: "phone",
      label: t("phone"),
    },
    {
      id: "email",
      label: t("email"),
    },
    {
      id: "address",
      label: t("address"),
    },
    {
      id: "city",
      label: t("city"),
    },
    {
      id: "state",
      label: t("state"),
    },
    {
      id: "zip",
      label: t("zip"),
    },
    {
      id: "notes",
      label: t("notes"),
    },
  ];

  // Print columns exclude Actions
  const printColumns = screenColumns.filter(
    (column) => column.id !== "actions"
  );

  //------------------------------------------------
  // Action Buttons
  //------------------------------------------------
  const ActionComp = (data) => (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.5,
      }}
    >
      {/* View */}
      <Tooltip arrow title={t("view")}>
        <IconButton
          onClick={() => handleView(data)}
          size="small"
          className="btn-primary"
        >
          <VisibilityRounded className="fs-18 m-2" />
        </IconButton>
      </Tooltip>

      {/* Edit */}
      <Tooltip arrow title={t("editThisCustomer")}>
        <IconButton
          onClick={() => handleAddEdit(data)}
          size="small"
          className="btn-primary"
        >
          <EditRounded className="fs-18 m-2" />
        </IconButton>
      </Tooltip>

      {/* Delete */}
      <Tooltip arrow title={t("deleteThisCustomer")}>
        <IconButton
          onClick={() => handleDlt(data.id)}
          size="small"
          className="btn-error"
        >
          <ClearRounded className="fs-18 m-2" />
        </IconButton>
      </Tooltip>
    </Box>
  );

  //------------------------------------------------
  // Add Customer Button
  //------------------------------------------------
  const HeaderComp = () => (
    <Button
      onClick={() => handleAddEdit(null)}
      className="btn-secondary"
      sx={{
        width: { md: "180px", xs: "100%" },
        mb: { xs: "10px", md: "0px" },
        fontSize: "17px",
        padding: "8px",
      }}
    >
      {t("addCustomer")}
    </Button>
  );

  //------------------------------------------------
  // Render
  //------------------------------------------------
  return (
    <>
      {/* Heading and Print Preview */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
          mb: 2,
        }}
      >
        <Typography variant="h5">
          {t("customers", {
            defaultValue: "Customers",
          })}
        </Typography>

        <ReportPrintButton
          reportRef={reportRef}
          paperSize="Letter"
          reportTitle={t("customers", {
            defaultValue: "Customers",
          })}
        />
      </Box>

      {/* Normal Customer Table */}
      <Table
        ActionComp={ActionComp}
        HeaderComp={HeaderComp}
        tblHead={screenColumns}
        tblData={tableData}
      />

      {/* Printable Customer List */}
      <Box
        ref={reportRef}
        className="customers-report-print"
        sx={{
          position: "absolute",
          left: "-10000px",
          top: 0,
          width: "1000px",
          backgroundColor: "#fff",
          color: "#000",
          p: 2,
        }}
      >
        <Typography
          variant="h5"
          sx={{
            textAlign: "center",
            fontWeight: "bold",
            mb: 2,
          }}
        >
          {t("customers", {
            defaultValue: "Customers",
          })}
        </Typography>

        <Table
          printMode={true}
          ActionComp={() => <div />}
          HeaderComp={() => <div />}
          tblHead={printColumns}
          tblData={tableData}
        />
      </Box>

      {/* Alert */}
      <Alert
        alert={alert}
        setAlert={setAlert}
      />

      {/* View Customer Dialog */}
      {showView && viewData && (
        <Dialog
          show={showView}
          onClose={handleCloseView}
          maxWidth="md"
        >
          <Box sx={{ p: 3 }}>
            {/* Details Print Preview Button */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                mb: 2,
              }}
            >
              <ReportPrintButton
                reportRef={detailsRef}
                paperSize="Letter"
                reportTitle={t("customerDetails", {
                  defaultValue: "Customer Details",
                })}
              />
            </Box>

            {/* Screen Customer Details */}
            <Box
              sx={{
                backgroundColor: "#fff",
                color: "#000",
                p: 2,
              }}
            >
              <CustomerDetailsContent
                customer={viewData}
                t={t}
              />
            </Box>

            {/* Printable Customer Details */}
            <Box
              ref={detailsRef}
              className="customer-details-print"
              sx={{
                position: "absolute",
                left: "-10000px",
                top: 0,
                width: "1000px",
                backgroundColor: "#fff",
                color: "#000",
                p: 2,
              }}
            >
              <CustomerDetailsContent
                customer={viewData}
                t={t}
              />
            </Box>

            {/* Close Button */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                mt: 3,
              }}
            >
              <Button
                variant="contained"
                onClick={handleCloseView}
              >
                {t("close")}
              </Button>
            </Box>
          </Box>
        </Dialog>
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  _customersList: state.pos.customersList,
  _store: state.pos.defStore,
});

const mapActionToProps = {
  _getById: actions.getById,
  _delete: actions.Delete,
};

export default connect(
  mapStateToProps,
  mapActionToProps
)(Customers);
